import React, { useState, useEffect, useRef } from "react";
import * as Yup from "yup";
import { Formik, Form, Field } from "formik";
import { toast } from "react-toastify";
import Mustache from "mustache";

import {
  makeStyles,
  Button,
  TextField,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  CircularProgress,
  Typography,
  Box,
  Chip,
  Divider,
  FormControlLabel,
  Switch,
  Tooltip
} from "@material-ui/core";
import { 
    green 
} from "@material-ui/core/colors";
import { 
    Lock as LockIcon, 
    Public as PublicIcon 
} from "@material-ui/icons";

import { i18n } from "../../translate/i18n";
import api from "../../services/api";
import toastError from "../../errors/toastError";

const useStyles = makeStyles((theme) => ({
  root: {
    flexWrap: "wrap",
  },
  textField: {
    width: "100%",
  },
  btnWrapper: {
    position: "relative",
  },
  buttonProgress: {
    color: green[500],
    position: "absolute",
    top: "50%",
    left: "50%",
    marginTop: -12,
    marginLeft: -12,
  },
  textQuickAnswerContainer: {
    width: "100%",
    marginBottom: theme.spacing(2),
  },
  chipContainer: {
    display: "flex",
    flexWrap: "wrap",
    gap: theme.spacing(1),
    marginBottom: theme.spacing(1),
  },
  divider: { margin: theme.spacing(2, 0) },
  previewBox: {
    border: "1px solid " + theme.palette.divider,
    borderRadius: theme.shape.borderRadius,
    padding: theme.spacing(2),
    marginTop: theme.spacing(1),
    backgroundColor: theme.palette.background.default,
  },
  infoText: {
    color: theme.palette.text.secondary,
    fontSize: "0.8rem",
  },
  privacySwitch: {
      marginBottom: theme.spacing(2),
      display: "flex",
      alignItems: "center"
  }
}));

const QuickAnswerSchema = Yup.object().shape({
  shortcut: Yup.string()
    .min(2, "¡Demasiado corto!")
    .max(30, "¡Demasiado largo!")
    .required("Este campo es obligatorio"),
  message: Yup.string()
    .min(2, "¡Demasiado corto!")
    .max(30000, "¡Demasiado largo!")
    .required("Este campo es obligatorio"),
  isPrivate: Yup.boolean().default(false)
});

const PreviewUpdater = ({ message, previewData, setMessagePreview }) => {
  useEffect(() => {
    const originalEscape = Mustache.escape;
    Mustache.escape = text => text;
    try {
        setMessagePreview(Mustache.render(message, previewData));
    } catch(e) {}
    Mustache.escape = originalEscape;
  }, [message, previewData, setMessagePreview]);

  return null;
};

const QuickAnswersModal = ({
  open,
  onClose,
  quickAnswerId,
  initialValues,
  onSave,
}) => {
  const classes = useStyles();
  const formikRef = useRef(null);
  const messageInputRef = useRef(null);
  const isMounted = useRef(true);

  const initialState = {
    shortcut: "",
    message: "",
    isPrivate: false,
  };

  const [quickAnswer, setQuickAnswer] = useState(initialState);
  const [messagePreview, setMessagePreview] = useState("");
  
  const availableVariables = ["nombreContacto", "numeroContacto", "emailContacto", "Link"];
  const previewData = {
      nombreContacto: "Juan Pérez",
      numeroContacto: "123456789",
      emailContacto: "juan.perez@ejemplo.com",
      Link: "https://ejemplo.com/perfil-de-juan"
  };

  useEffect(() => {
    return () => { isMounted.current = false; };
  }, []);

  useEffect(() => {
    const fetchQuickAnswer = async () => {
      if (initialValues) {
        setQuickAnswer((prevState) => ({ ...prevState, ...initialValues }));
      }
      if (!quickAnswerId) {
          if (!initialValues) setQuickAnswer(initialState);
          return;
      }
      try {
        const { data } = await api.get("/quickAnswers/" + quickAnswerId);
        if (isMounted.current) {
          setQuickAnswer(data);
        }
      } catch (err) {
        toastError(err);
      }
    };
    fetchQuickAnswer();
  }, [quickAnswerId, open, initialValues]);

  const handleClose = () => {
    onClose();
    setQuickAnswer(initialState);
  };

  const handleSaveQuickAnswer = async (values) => {
    try {
      if (quickAnswerId) {
        await api.put("/quickAnswers/" + quickAnswerId, values);
      } else {
        const { data } = await api.post("/quickAnswers", values);
        if (onSave) {
          onSave(data);
        }
      }
      toast.success(i18n.t("quickAnswersModal.success"));
      handleClose();
    } catch (err) {
      toastError(err);
    }
  };

  const handleInsertVariable = (tag) => {
    if (!formikRef.current || !messageInputRef.current) return;

    const cursorPosition = messageInputRef.current.selectionStart || 0;
    const currentValue = formikRef.current.values.message;
    
    const newValue = 
        currentValue.substring(0, cursorPosition) + 
        tag + 
        currentValue.substring(cursorPosition);
        
    formikRef.current.setFieldValue("message", newValue);

    setTimeout(() => {
      if (messageInputRef.current) {
        messageInputRef.current.focus();
        const newCursorPos = cursorPosition + tag.length;
        messageInputRef.current.setSelectionRange(newCursorPos, newCursorPos);
      }
    }, 100);
  };
  
  return (
    <div className={classes.root}>
      <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth scroll="paper">
        <DialogTitle id="form-dialog-title">
          {quickAnswerId ? i18n.t("quickAnswersModal.title.edit") : i18n.t("quickAnswersModal.title.add")}
        </DialogTitle>
        <Formik
          innerRef={formikRef}
          initialValues={quickAnswer}
          enableReinitialize={true}
          validationSchema={QuickAnswerSchema}
          onSubmit={(values, actions) => {
            setTimeout(() => {
              handleSaveQuickAnswer(values);
              actions.setSubmitting(false);
            }, 400);
          }}
        >
          {({ values, errors, touched, isSubmitting, setFieldValue }) => {
            return (
              <Form>
                <PreviewUpdater
                  message={values.message}
                  previewData={previewData}
                  setMessagePreview={setMessagePreview}
                />
                <DialogContent dividers>
                  <div className={classes.privacySwitch}>
                    <FormControlLabel
                      control={
                        <Switch
                          checked={values.isPrivate}
                          onChange={(e) => setFieldValue("isPrivate", e.target.checked)}
                          color="primary"
                        />
                      }
                      label={
                        <Box display="flex" alignItems="center">
                          <Typography variant="body1" style={{ marginRight: 8 }}>
                            {values.isPrivate ? "Respuesta Privada" : "Respuesta Pública"}
                          </Typography>
                          {values.isPrivate ? (
                            <Tooltip title="Solo tú podrás ver y usar esta respuesta">
                              <LockIcon color="action" fontSize="small" />
                            </Tooltip>
                          ) : (
                            <Tooltip title="Todos los usuarios de la compañía podrán usar esta respuesta">
                              <PublicIcon color="action" fontSize="small" />
                            </Tooltip>
                          )}
                        </Box>
                      }
                    />
                  </div>

                  <Box mb={2} mt={-1}>
                    <Typography variant="caption" color="textSecondary" style={{ display: 'block', lineHeight: 1.3 }}>
                      {values.isPrivate 
                        ? "⚠️ Esta respuesta será visible y utilizable únicamente por ti." 
                        : "ℹ️ Esta respuesta estará disponible para todos los usuarios de la compañía."}
                    </Typography>
                  </Box>

                  <div className={classes.textQuickAnswerContainer}>
                    <Field
                      as={TextField}
                      label={i18n.t("quickAnswersModal.form.shortcut")}
                      name="shortcut"
                      autoFocus
                      error={touched.shortcut && Boolean(errors.shortcut)}
                      helperText={touched.shortcut && errors.shortcut}
                      variant="outlined"
                      margin="dense"
                      className={classes.textField}
                    />
                  </div>
                  
                  <Divider className={classes.divider} />
                  
                  <Typography variant="subtitle1" gutterBottom>
                    Variables Disponibles
                  </Typography>
                  <Box className={classes.chipContainer}>
                    {availableVariables.map((variable) => {
                      const isLink = variable.toLowerCase() === "link";
                      const tag = isLink ? "{{{" + variable + "}}}" : "{{" + variable + "}}";
                      return (
                        <Chip
                          key={variable}
                          label={tag}
                          onClick={() => handleInsertVariable(tag)}
                          variant="outlined"
                          size="small"
                        />
                      );
                    })}
                  </Box>
                  <Typography variant="caption" className={classes.infoText}>
                    Las variables se reemplazarán con la información del contacto al usar la respuesta.
                  </Typography>

                  <div className={classes.textQuickAnswerContainer}>
                    <Field
                      as={TextField}
                      label={i18n.t("quickAnswersModal.form.message")}
                      name="message"
                      error={touched.message && Boolean(errors.message)}
                      helperText={touched.message && errors.message}
                      inputRef={messageInputRef}
                      variant="outlined"
                      margin="dense"
                      className={classes.textField}
                      multiline
                      rows={5}
                    />
                  </div>

                  <Box className={classes.previewBox}>
                      <Typography variant="subtitle2" gutterBottom>Vista Previa:</Typography>
                      <Typography variant="body2" style={{ whiteSpace: "pre-wrap" }}>
                        {messagePreview}
                      </Typography>
                  </Box>

                </DialogContent>
                <DialogActions>
                  <Button onClick={handleClose} color="secondary" disabled={isSubmitting} variant="outlined">
                    {i18n.t("quickAnswersModal.buttons.cancel")}
                  </Button>
                  <Button type="submit" color="primary" disabled={isSubmitting} variant="contained" className={classes.btnWrapper}>
                    {quickAnswerId ? i18n.t("quickAnswersModal.buttons.okEdit") : i18n.t("quickAnswersModal.buttons.okAdd")}
                    {isSubmitting && <CircularProgress size={24} className={classes.buttonProgress} />}
                  </Button>
                </DialogActions>
              </Form>
            );
          }}
        </Formik>
      </Dialog>
    </div>
  );
};

export default QuickAnswersModal;